import { useEffect, useMemo, useState } from 'react';
import { isToday, formatDate, isMultiDayEvent, isAllDayEvent, getEventCardStyle, isSameDay, isEventPast } from '../../utils/calendar';
import { formatTime } from '../../utils/time';
import { CalendarEvent } from '../../types/calendar';
import { getDesignTokens } from '../../utils/designTokens';
import MemberAvatar from '../MemberAvatar';
import { getDateTypeIcon } from './EventCard';
import { formatRecurringPattern } from '../../utils/recurrence';
import { Icon } from '../../utils/icons';
import WeatherWidget from '../WeatherWidget';
import { useTodoStore } from '../../store/todoStore';
import { Todo } from '../../types/todo';
import { useFamilyStore } from '../../store/familyStore';
import LoadingSpinner from '../LoadingSpinner';
import { useToastStore } from '../../store/toastStore';

interface DayViewProps {
  date: Date;
  events: CalendarEvent[];
  onDateClick?: (date: Date, hour?: number) => void;
  onEventClick?: (event: CalendarEvent) => void;
  isDarkMode?: boolean;
}

export default function DayView({
  date,
  events,
  onDateClick,
  onEventClick,
  isDarkMode = false,
}: DayViewProps) {
  const isDateToday = isToday(date);
  const { todos, fetchTodos, updateTodo } = useTodoStore();
  const { family } = useFamilyStore();
  const familyMembers = family?.members || [];
  const { showToast } = useToastStore();
  const [pendingStatusIds, setPendingStatusIds] = useState<Record<string, boolean>>({});

  // Filter events for this date
  const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
  const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);

  // Fetch todos when date changes
  useEffect(() => {
    fetchTodos().catch((error) => {
      console.error('Error fetching todos:', error);
      showToast(error instanceof Error ? error.message : 'Failed to load todos', 'error');
    });
  }, [date, fetchTodos, showToast]);

  // Filter todos for this day (always include past due todos)
  const dayTodos = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return todos.filter(todo => {
      // Only show open todos (not done)
      if (todo.status === 'done') return false;
      
      if (!todo.dueDateTime) return false; // Only show todos with due dates
      // Parse dueDateTime, handling both Date objects and ISO strings
      // If it's an ISO string, extract date components directly to avoid timezone shifts
      let dueDate: Date;
      if (todo.dueDateTime instanceof Date) {
        dueDate = todo.dueDateTime;
      } else if (typeof todo.dueDateTime === 'string') {
        const dueStr = todo.dueDateTime;
        // Check if it's a date-only string (YYYY-MM-DD) or full ISO string
        if (dueStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // Date-only string: parse as local date
          const [year, month, day] = dueStr.split('-').map(Number);
          dueDate = new Date(year, month - 1, day);
        } else {
          // Full ISO string: extract date components from UTC to avoid timezone shifts
          const isoDate = new Date(dueStr);
          // Use UTC methods to get the date components, then create local date
          const year = isoDate.getUTCFullYear();
          const month = isoDate.getUTCMonth();
          const day = isoDate.getUTCDate();
          dueDate = new Date(year, month, day);
        }
      } else {
        dueDate = new Date();
      }
      const dueDateStart = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate(), 0, 0, 0);
      
      // Always show past due todos, and also show todos due on the selected date
      const isPastDue = dueDateStart.getTime() < today.getTime();
      const isDueOnSelectedDate = dueDateStart.getTime() >= dayStart.getTime() && dueDateStart.getTime() <= dayEnd.getTime();
      
      return isPastDue || isDueOnSelectedDate;
    }).sort((a, b) => {
      // First, separate past due from todos due on selected date (past due first)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const aDueDate = a.dueDateTime instanceof Date ? a.dueDateTime : (a.dueDateTime ? new Date(a.dueDateTime) : new Date());
      const aDueDateStart = new Date(aDueDate.getFullYear(), aDueDate.getMonth(), aDueDate.getDate(), 0, 0, 0);
      const aIsPastDue = aDueDateStart.getTime() < today.getTime();
      
      const bDueDate = b.dueDateTime instanceof Date ? b.dueDateTime : (b.dueDateTime ? new Date(b.dueDateTime) : new Date());
      const bDueDateStart = new Date(bDueDate.getFullYear(), bDueDate.getMonth(), bDueDate.getDate(), 0, 0, 0);
      const bIsPastDue = bDueDateStart.getTime() < today.getTime();
      
      if (aIsPastDue !== bIsPastDue) {
        return aIsPastDue ? -1 : 1; // Past due first
      }
      
      // Sort by status (open first), then priority, then due time
      if (a.status !== b.status) {
        return a.status === 'open' ? -1 : 1;
      }
      const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      const aPriority = priorityOrder[a.priority || 'medium'];
      const bPriority = priorityOrder[b.priority || 'medium'];
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      if (a.dueDateTime && b.dueDateTime) {
        const aTime = a.dueDateTime instanceof Date ? a.dueDateTime : new Date(a.dueDateTime);
        const bTime = b.dueDateTime instanceof Date ? b.dueDateTime : new Date(b.dueDateTime);
        return aTime.getTime() - bTime.getTime();
      }
      return 0;
    });
  }, [todos, dayStart, dayEnd]);
  
  const dayEvents = events.filter(event => {
    const eventStart = event.startTime instanceof Date ? event.startTime : new Date(event.startTime);
    const eventEnd = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);
    // Event overlaps if: eventStart <= dayEnd AND eventEnd >= dayStart
    return eventStart.getTime() <= dayEnd.getTime() && eventEnd.getTime() >= dayStart.getTime();
  });

  // Separate events into: all-day events, multi-day timed events, and today-only timed events
  const allDayEvents: CalendarEvent[] = [];
  const multiDayTimedEvents: CalendarEvent[] = [];
  const todayOnlyEvents: CalendarEvent[] = [];

  dayEvents.forEach(event => {
    const eventStart = event.startTime instanceof Date ? event.startTime : new Date(event.startTime);
    const eventEnd = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);
    const isAllDay = isAllDayEvent(eventStart, eventEnd, event.isAllDay);
    const isMultiDay = isMultiDayEvent(eventStart, eventEnd);
    
    // All-day events (both single-day and multi-day) go into allDayEvents
    if (isAllDay) {
      allDayEvents.push(event);
    } else if (isMultiDay) {
      // Multi-day timed events
      multiDayTimedEvents.push(event);
    } else {
      // Check if event starts and ends on the selected date
      const startsOnDate = eventStart >= dayStart && eventStart <= dayEnd;
      const endsOnDate = eventEnd >= dayStart && eventEnd <= dayEnd;
      
      if (startsOnDate && endsOnDate) {
        todayOnlyEvents.push(event);
      } else {
        // Event that overlaps but doesn't fully fit - treat as multi-day
        multiDayTimedEvents.push(event);
      }
    }
  });

  // Sort each section: upcoming first, past last, then chronologically by start time
  allDayEvents.sort((a, b) => {
    const aIsPast = isEventPast(a);
    const bIsPast = isEventPast(b);
    if (aIsPast !== bIsPast) {
      return aIsPast ? 1 : -1; // Upcoming events first
    }
    const aStart = a.startTime instanceof Date ? a.startTime : new Date(a.startTime);
    const bStart = b.startTime instanceof Date ? b.startTime : new Date(b.startTime);
    return aStart.getTime() - bStart.getTime();
  });

  multiDayTimedEvents.sort((a, b) => {
    const aIsPast = isEventPast(a);
    const bIsPast = isEventPast(b);
    if (aIsPast !== bIsPast) {
      return aIsPast ? 1 : -1; // Upcoming events first
    }
    const aStart = a.startTime instanceof Date ? a.startTime : new Date(a.startTime);
    const bStart = b.startTime instanceof Date ? b.startTime : new Date(b.startTime);
    return aStart.getTime() - bStart.getTime();
  });

  todayOnlyEvents.sort((a, b) => {
    const aIsPast = isEventPast(a);
    const bIsPast = isEventPast(b);
    if (aIsPast !== bIsPast) {
      return aIsPast ? 1 : -1; // Upcoming events first
    }
    const aStart = a.startTime instanceof Date ? a.startTime : new Date(a.startTime);
    const bStart = b.startTime instanceof Date ? b.startTime : new Date(b.startTime);
    return aStart.getTime() - bStart.getTime();
  });

  const tokens = getDesignTokens(isDarkMode);
  const theme = {
    bg: tokens.card.bg,
    border: tokens.border.light,
    text: tokens.text.primary,
    textMuted: tokens.text.muted,
    textSecondary: tokens.text.muted,
    today: isDarkMode 
      ? 'bg-[rgba(96,165,250,0.2)] border-[rgba(96,165,250,0.5)]' 
      : 'bg-[rgba(217,119,111,0.15)] border-[rgba(217,119,111,0.4)]',
    sectionHeader: tokens.text.primary,
    eventBorder: tokens.border.light,
    eventHover: isDarkMode ? 'hover:bg-[rgba(255,255,255,0.05)]' : 'hover:bg-[rgba(0,0,0,0.02)]',
      };


  // Get important date border style
  const getImportantDateBorder = (event: CalendarEvent): string => {
    if (!event.isImportantDate) return '';
    
    const dateType = event.dateType;
    const priority = event.priority || 'medium';
    
    if (isDarkMode) {
      switch (dateType) {
        case 'birthday':
          return 'border-pink-400/60 shadow-pink-500/20';
        case 'anniversary':
          return 'border-purple-400/60 shadow-purple-500/20';
        case 'visit':
          return 'border-blue-400/60 shadow-blue-500/20';
        case 'holiday':
          return 'border-yellow-400/60 shadow-yellow-500/20';
        case 'school':
          return 'border-green-400/60 shadow-green-500/20';
        case 'medical':
          return 'border-red-400/60 shadow-red-500/20';
        case 'milestone':
          return 'border-amber-400/60 shadow-amber-500/20';
        default:
          return priority === 'critical' ? 'border-red-400/60 shadow-red-500/20' : 'border-[#d9776f]/60 shadow-[#d9776f]/20';
      }
    } else {
      switch (dateType) {
        case 'birthday':
          return 'border-pink-400 shadow-pink-200';
        case 'anniversary':
          return 'border-purple-400 shadow-purple-200';
        case 'visit':
          return 'border-blue-400 shadow-blue-200';
        case 'holiday':
          return 'border-yellow-400 shadow-yellow-200';
        case 'school':
          return 'border-green-400 shadow-green-200';
        case 'medical':
          return 'border-red-400 shadow-red-200';
        case 'milestone':
          return 'border-amber-400 shadow-amber-200';
        default:
          return priority === 'critical' ? 'border-red-400 shadow-red-200' : 'border-[#d9776f] shadow-[#d9776f]/20';
      }
    }
  };

  // Render all-day events using the exact same styling as regular events
  const renderAllDayEvent = (event: CalendarEvent) => {
    const members = event.members && event.members.length > 0
      ? event.members
      : (event.memberId ? [{
          memberId: event.memberId,
          memberName: event.memberName || '',
          memberColor: event.memberColor || '#3b82f6',
          memberAvatar: event.memberAvatar,
        }] : []);

    const eventStart = event.startTime instanceof Date ? event.startTime : new Date(event.startTime);
    const eventEnd = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);
    const isMultiDay = isMultiDayEvent(eventStart, eventEnd);
    const isImportantDate = event.isImportantDate || false;
    const isPastEvent = isEventPast(event);
    const importantDateBorder = isImportantDate ? getImportantDateBorder(event) : '';
    const dateTypeIcon = getDateTypeIcon(event.dateType);
    const recurringPatternText = event.isRecurring ? formatRecurringPattern(event.recurringPattern) : '';

    // Calculate the current day's position within a multi-day event
    const dayStartBoundary = new Date(date);
    dayStartBoundary.setHours(0, 0, 0, 0);
    const eventStartDay = new Date(eventStart);
    eventStartDay.setHours(0, 0, 0, 0);
    const eventEndDay = new Date(eventEnd);
    eventEndDay.setHours(0, 0, 0, 0);

    const msPerDay = 24 * 60 * 60 * 1000;
    const totalDays = Math.max(1, Math.round((eventEndDay.getTime() - eventStartDay.getTime()) / msPerDay) + 1);
    const currentDayIndex = Math.min(
      totalDays,
      Math.max(1, Math.round((dayStartBoundary.getTime() - eventStartDay.getTime()) / msPerDay) + 1)
    );

    const isStartToday = isSameDay(eventStart, date);
    const isEndToday = isSameDay(eventEnd, date);

    // Use exact same styling calculations as renderEvent
    const containerPadding = isPastEvent ? 'p-2 sm:p-2.5 min-h-[36px]' : 'p-4 sm:p-5 min-h-[60px]';
    const timePrimarySize = isPastEvent ? 'text-xs sm:text-sm' : 'text-base sm:text-lg';
    const timeSecondarySize = isPastEvent ? 'text-[10px] sm:text-[11px]' : 'text-sm sm:text-base';
    const titleSize = isPastEvent ? 'text-sm sm:text-base' : 'text-lg sm:text-xl';

    const cardStyle = getEventCardStyle(members, isDarkMode);
    const hasLeftBar = cardStyle.leftBarGradient;
    const leftBarWidth = cardStyle.leftBarWidth ? parseInt(cardStyle.leftBarWidth.replace('px', '')) : 0;
    const paddingLeft = hasLeftBar && leftBarWidth > 0 ? `${leftBarWidth + 16}px` : undefined;
    
    // Ensure border color is consistent - use explicit border color in style to avoid any visual differences
    const defaultBorderColor = isDarkMode ? 'rgba(107, 114, 128, 0.3)' : 'rgba(141, 125, 107, 0.25)'; // Peachy warm tone
    
    return (
      <div
        key={event.id}
        className={`relative w-full ${containerPadding} rounded-landing border-2 ${importantDateBorder || ''} cursor-pointer touch-manipulation active:scale-[0.98] transition-all ${theme.eventHover} flex items-start gap-4 overflow-hidden ${isImportantDate && !isMultiDay ? 'shadow-md' : ''} ${isPastEvent ? 'opacity-80' : ''}`}
        style={{
          backgroundColor: cardStyle.background || (isDarkMode ? 'rgba(31, 41, 55, 1)' : 'rgba(255, 254, 251, 1)'),
          background: cardStyle.background || (isDarkMode ? 'rgba(31, 41, 55, 1)' : 'rgba(255, 254, 251, 1)'),
          paddingLeft: paddingLeft,
          ...(importantDateBorder ? {} : { borderColor: defaultBorderColor }),
        }}
        onClick={() => onEventClick?.(event)}
      >
        {/* Left bar gradient overlay for multi-member events */}
        {hasLeftBar && (
          <div
            className="absolute left-0 top-0 bottom-0"
            style={{
              width: cardStyle.leftBarWidth || '4px',
              background: cardStyle.leftBarGradient,
              borderTopLeftRadius: '18px', // Match rounded-landing (18px)
              borderBottomLeftRadius: '18px', // Match rounded-landing (18px)
              zIndex: 1,
            }}
          />
        )}
        {/* Multi-day indicator: Top-left corner triangle */}
        {isMultiDay && (
          <div
            className="absolute top-0 left-0 pointer-events-none"
            style={{
              width: 0,
              height: 0,
              borderTop: isDarkMode 
                ? '20px solid rgba(251, 191, 36, 0.3)' // Subtle amber for dark mode
                : '20px solid rgba(251, 191, 36, 0.25)', // Subtle amber for light mode
              borderRight: '20px solid transparent',
              zIndex: 10,
            }}
          />
        )}
        
        {/* Time column - show "All day" */}
        <div className="flex-shrink-0 w-24 sm:w-28">
          <div className={`${theme.text} ${timePrimarySize} font-semibold`}>
            All day
          </div>
        </div>

        {/* Event content */}
        <div className="flex-1 min-w-0">
          <div className={`${theme.text} ${titleSize} font-bold mb-1 flex items-center gap-2 flex-wrap`}>
            {/* Date Type Icon for Important Dates */}
            {isImportantDate && dateTypeIcon && (
              <span title={event.dateType || undefined}>
                <Icon name={dateTypeIcon} size="lg" className={isDarkMode ? 'text-gray-100' : 'text-gray-600'} />
              </span>
            )}
            {/* Recurring indicator */}
            {event.isRecurring && recurringPatternText && (
              <span
                className={`${theme.textMuted} text-xs sm:text-sm font-semibold flex items-center gap-1`}
                title={recurringPatternText}
              >
                <span aria-hidden="true" className="opacity-75">↻</span>
                <span className="hidden sm:inline">{recurringPatternText}</span>
              </span>
            )}
            {/* Member Avatars - Horizontal stack before event name */}
            {members.length > 0 && (
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {members.map((member, idx) => (
                  <MemberAvatar
                    key={member.memberId || idx}
                    member={member}
                    size="md"
                  />
                ))}
              </div>
            )}
            <span className="leading-tight">{event.title}</span>

            {isMultiDay && (
              <span className={`text-xs sm:text-sm font-semibold px-2 py-1 rounded-sm-full ${
                isDarkMode 
                  ? 'bg-yellow-900/20 text-yellow-200/70' 
                  : 'bg-yellow-50 text-yellow-700'
              }`}>
                Day {currentDayIndex} of {totalDays}
              </span>
            )}
          </div>

          {isMultiDay && (
            <div className={`${theme.textSecondary} text-xs sm:text-sm font-semibold flex items-center gap-2 mb-2`}>
              <span className="truncate">
                {formatDate(eventStart, 'short')} - {formatDate(eventEnd, 'short')}
              </span>
            </div>
          )}
          
          {event.location && (
            <div className={`${theme.textSecondary} text-sm sm:text-base mb-2 flex items-center gap-2`}>
              <span>📍</span>
              <span>{event.location}</span>
            </div>
          )}
          
          {event.description && (
            <div className={`${theme.textMuted} text-sm sm:text-base line-clamp-2`}>
              {event.description}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderEvent = (event: CalendarEvent) => {
    const members = event.members && event.members.length > 0
      ? event.members
      : (event.memberId ? [{
          memberId: event.memberId,
          memberName: event.memberName || '',
          memberColor: event.memberColor || '#3b82f6',
          memberAvatar: event.memberAvatar,
        }] : []);

    const eventStart = event.startTime instanceof Date ? event.startTime : new Date(event.startTime);
    const eventEnd = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);
    const isMultiDay = isMultiDayEvent(eventStart, eventEnd);
    const isAllDay = isAllDayEvent(eventStart, eventEnd, event.isAllDay);
    const isImportantDate = event.isImportantDate || false;
    const isPastEvent = isEventPast(event);
    const importantDateBorder = isImportantDate ? getImportantDateBorder(event) : '';
    const dateTypeIcon = getDateTypeIcon(event.dateType);
    const recurringPatternText = event.isRecurring ? formatRecurringPattern(event.recurringPattern) : '';

    // Calculate the current day's position within a multi-day event
    const dayStartBoundary = new Date(date);
    dayStartBoundary.setHours(0, 0, 0, 0);
    const eventStartDay = new Date(eventStart);
    eventStartDay.setHours(0, 0, 0, 0);
    const eventEndDay = new Date(eventEnd);
    eventEndDay.setHours(0, 0, 0, 0);

    const msPerDay = 24 * 60 * 60 * 1000;
    const totalDays = Math.max(1, Math.round((eventEndDay.getTime() - eventStartDay.getTime()) / msPerDay) + 1);
    const currentDayIndex = Math.min(
      totalDays,
      Math.max(1, Math.round((dayStartBoundary.getTime() - eventStartDay.getTime()) / msPerDay) + 1)
    );

    const isStartToday = isSameDay(eventStart, date);
    const isEndToday = isSameDay(eventEnd, date);

    let primaryTimeLabel = '';
    let secondaryTimeLabel = '';

    if (isAllDay && !isMultiDay) {
      // Single-day all-day events: show "All day"
      primaryTimeLabel = 'All day';
    } else if (!isMultiDay) {
      // Single-day timed events: show time range
      primaryTimeLabel = formatTime(eventStart);
      if (eventEnd) {
        secondaryTimeLabel = formatTime(eventEnd);
      }
    } else {
      // Multi-day events (both all-day and timed): use consistent multi-day styling
      if (isAllDay) {
        // All-day multi-day events: show date range info like timed multi-day
        if (isStartToday && isEndToday) {
          primaryTimeLabel = 'All day';
        } else if (isStartToday) {
          primaryTimeLabel = 'Starts today';
          secondaryTimeLabel = `Ends ${formatDate(eventEnd, 'short')}`;
        } else if (isEndToday) {
          primaryTimeLabel = 'Ends today';
        } else {
          primaryTimeLabel = 'Continues';
          secondaryTimeLabel = `${formatDate(eventStart, 'short')} - ${formatDate(eventEnd, 'short')}`;
        }
      } else {
        // Timed multi-day events: show time info
        if (isStartToday && isEndToday) {
          primaryTimeLabel = `${formatTime(eventStart)} - ${formatTime(eventEnd)}`;
        } else if (isStartToday) {
          primaryTimeLabel = `Starts ${formatTime(eventStart)}`;
          secondaryTimeLabel = `Ends ${formatDate(eventEnd, 'short')} ${formatTime(eventEnd)}`;
        } else if (isEndToday) {
          primaryTimeLabel = `Ends ${formatTime(eventEnd)}`;
        } else {
          primaryTimeLabel = 'Continues';
          secondaryTimeLabel = `${formatDate(eventStart, 'short')} - ${formatDate(eventEnd, 'short')}`;
        }
      }
    }

    const shouldShowTimeColumn = !!primaryTimeLabel;
    const containerPadding = isPastEvent ? 'p-2 sm:p-2.5 min-h-[36px]' : 'p-4 sm:p-5 min-h-[60px]';
    const timePrimarySize = isPastEvent ? 'text-xs sm:text-sm' : 'text-base sm:text-lg';
    const timeSecondarySize = isPastEvent ? 'text-[10px] sm:text-[11px]' : 'text-sm sm:text-base';
    const titleSize = isPastEvent ? 'text-sm sm:text-base' : 'text-lg sm:text-xl';

    const cardStyle = getEventCardStyle(members, isDarkMode);
    const hasLeftBar = cardStyle.leftBarGradient;
    const leftBarWidth = cardStyle.leftBarWidth ? parseInt(cardStyle.leftBarWidth.replace('px', '')) : 0;
    const paddingLeft = hasLeftBar && leftBarWidth > 0 ? `${leftBarWidth + 16}px` : undefined;
    
    // Ensure border color is consistent - use explicit border color in style to avoid any visual differences
    const defaultBorderColor = isDarkMode ? 'rgba(107, 114, 128, 0.3)' : 'rgba(141, 125, 107, 0.25)'; // Peachy warm tone
    
    return (
      <div
        key={event.id}
        className={`relative w-full ${containerPadding} rounded-landing border-2 ${importantDateBorder || ''} cursor-pointer touch-manipulation active:scale-[0.98] transition-all ${theme.eventHover} flex items-start gap-4 overflow-hidden ${isImportantDate && !isMultiDay ? 'shadow-md' : ''} ${isPastEvent ? 'opacity-80' : ''}`}
        style={{
          backgroundColor: cardStyle.background || (isDarkMode ? 'rgba(31, 41, 55, 1)' : 'rgba(255, 254, 251, 1)'),
          background: cardStyle.background || (isDarkMode ? 'rgba(31, 41, 55, 1)' : 'rgba(255, 254, 251, 1)'),
          paddingLeft: paddingLeft,
          ...(importantDateBorder ? {} : { borderColor: defaultBorderColor }),
        }}
        onClick={() => onEventClick?.(event)}
      >
        {/* Left bar gradient overlay for multi-member events */}
        {hasLeftBar && (
          <div
            className="absolute left-0 top-0 bottom-0"
            style={{
              width: cardStyle.leftBarWidth || '4px',
              background: cardStyle.leftBarGradient,
              borderTopLeftRadius: '18px', // Match rounded-landing (18px)
              borderBottomLeftRadius: '18px', // Match rounded-landing (18px)
              zIndex: 1,
            }}
          />
        )}
        {/* Multi-day indicator: Top-left corner triangle */}
        {isMultiDay && (
          <div
            className="absolute top-0 left-0 pointer-events-none"
            style={{
              width: 0,
              height: 0,
              borderTop: isDarkMode 
                ? '20px solid rgba(251, 191, 36, 0.3)' // Subtle amber for dark mode
                : '20px solid rgba(251, 191, 36, 0.25)', // Subtle amber for light mode
              borderRight: '20px solid transparent',
              zIndex: 10,
            }}
          />
        )}
        
        {/* Time column - contextual for multi-day/all-day */}
        {shouldShowTimeColumn && (
          <div className="flex-shrink-0 w-24 sm:w-28">
            <div className={`${theme.text} ${timePrimarySize} font-semibold`}>
              {primaryTimeLabel}
            </div>
            {secondaryTimeLabel && (
              <div className={`${theme.textMuted} ${timeSecondarySize} font-semibold mt-1`}>
                {secondaryTimeLabel}
              </div>
            )}
          </div>
        )}

        {/* Event content */}
        <div className="flex-1 min-w-0">
          <div className={`${theme.text} ${titleSize} font-bold mb-1 flex items-center gap-2 flex-wrap`}>
            {/* Date Type Icon for Important Dates */}
            {isImportantDate && dateTypeIcon && (
              <span title={event.dateType || undefined}>
                <Icon name={dateTypeIcon} size="lg" className={isDarkMode ? 'text-gray-100' : 'text-gray-600'} />
              </span>
            )}
            {/* Recurring indicator */}
            {event.isRecurring && recurringPatternText && (
              <span
                className={`${theme.textMuted} text-xs sm:text-sm font-semibold flex items-center gap-1`}
                title={recurringPatternText}
              >
                <span aria-hidden="true" className="opacity-75">↻</span>
                <span className="hidden sm:inline">{recurringPatternText}</span>
              </span>
            )}
            {/* Member Avatars - Horizontal stack before event name */}
            {members.length > 0 && (
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {members.map((member, idx) => (
                  <MemberAvatar
                    key={member.memberId || idx}
                    member={member}
                    size="md"
                  />
                ))}
              </div>
            )}
            <span className="leading-tight">{event.title}</span>

            {isMultiDay && (
              <span className={`text-xs sm:text-sm font-semibold px-2 py-1 rounded-sm-full ${
                isDarkMode 
                  ? 'bg-yellow-900/20 text-yellow-200/70' 
                  : 'bg-yellow-50 text-yellow-700'
              }`}>
                Day {currentDayIndex} of {totalDays}
              </span>
            )}
          </div>

          {isMultiDay && (
            <div className={`${theme.textSecondary} text-xs sm:text-sm font-semibold flex items-center gap-2 mb-2`}>
              <span className="truncate">
                {formatDate(eventStart, 'short')} - {formatDate(eventEnd, 'short')}
              </span>
            </div>
          )}
          
          {event.location && (
            <div className={`${theme.textSecondary} text-sm sm:text-base mb-2 flex items-center gap-2`}>
              <span>📍</span>
              <span>{event.location}</span>
            </div>
          )}
          
          {event.description && (
            <div className={`${theme.textMuted} text-sm sm:text-base line-clamp-2`}>
              {event.description}
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleTodoToggle = async (todoId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'open' ? 'done' : 'open';
    setPendingStatusIds((state) => ({ ...state, [todoId]: true }));
    updateTodo(todoId, { status: newStatus })
      .then(() => {
        showToast(newStatus === 'done' ? 'Todo marked done' : 'Todo reopened', 'success');
      })
      .catch((error) => {
        console.error('Error updating todo status:', error);
        showToast(error instanceof Error ? error.message : 'Failed to update todo', 'error');
      })
      .finally(() => {
        setPendingStatusIds((state) => {
          const next = { ...state };
          delete next[todoId];
          return next;
        });
      });
  };

  const renderTodo = (todo: Todo) => {
    // Check if todo is past due
    const isPastDue = (): boolean => {
      if (!todo.dueDateTime || todo.status === 'done') return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      // Parse dueDateTime, handling both Date objects and ISO strings
      let dueDate: Date;
      if (todo.dueDateTime instanceof Date) {
        dueDate = todo.dueDateTime;
      } else if (typeof todo.dueDateTime === 'string') {
        const dueStr = todo.dueDateTime;
        if (dueStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = dueStr.split('-').map(Number);
          dueDate = new Date(year, month - 1, day);
        } else {
          const isoDate = new Date(dueStr);
          dueDate = new Date(isoDate.getUTCFullYear(), isoDate.getUTCMonth(), isoDate.getUTCDate());
        }
      } else {
        dueDate = new Date();
      }
      const dueDateStart = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate(), 0, 0, 0);
      return dueDateStart.getTime() < today.getTime();
    };

    const pastDue = isPastDue();
    const priorityColors = {
      critical: isDarkMode ? 'bg-red-900/30 border-red-600/50' : 'bg-red-50 border-red-300',
      high: isDarkMode ? 'bg-orange-900/30 border-orange-600/50' : 'bg-orange-50 border-orange-300',
      medium: isDarkMode ? 'bg-blue-900/30 border-blue-600/50' : 'bg-blue-50 border-blue-300',
      low: isDarkMode ? 'bg-gray-800/30 border-gray-600/50' : 'bg-gray-50 border-gray-300',
    };
    const priorityColor = priorityColors[todo.priority || 'medium'];
    const isDone = todo.status === 'done';
    
    // Override colors for past due todos
    const pastDueColors = pastDue 
      ? (isDarkMode ? 'bg-red-900/40 border-red-500/70' : 'bg-red-50 border-red-400')
      : priorityColor;

    return (
      <div
        key={todo.todoId}
        className={`border rounded p-2 flex items-start gap-2 cursor-pointer touch-manipulation active:scale-[0.98] transition-all ${pastDueColors} ${isDone ? 'opacity-60' : ''}`}
        onClick={() => {
          if (!pendingStatusIds[todo.todoId]) {
            handleTodoToggle(todo.todoId, todo.status);
          }
        }}
      >
        <button
          className={`w-6 h-6 rounded-full border flex items-center justify-center flex-shrink-0 mt-0.5 ${
            isDone ? 'bg-green-500 border-green-600' : 'bg-white border-gray-300'
          }`}
          onClick={(e) => {
            e.stopPropagation();
            handleTodoToggle(todo.todoId, todo.status);
          }}
          aria-label="Toggle status"
          disabled={pendingStatusIds[todo.todoId]}
        >
          {pendingStatusIds[todo.todoId] ? (
            <LoadingSpinner size="sm" label="Updating todo" />
          ) : (
            isDone && <Icon name="check" size="sm" className="text-white" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className={`${theme.text} text-sm font-semibold ${isDone ? 'line-through' : ''}`}>
            {todo.title}
          </div>
          {todo.dueDateTime && (() => {
            const parseTodoDueDate = (dueDateTime: Date | string | undefined): Date | undefined => {
              if (!dueDateTime) return undefined;
              if (dueDateTime instanceof Date) {
                const isoString = dueDateTime.toISOString();
                if (isoString.endsWith('T00:00:00.000Z')) {
                  return new Date(dueDateTime.getUTCFullYear(), dueDateTime.getUTCMonth(), dueDateTime.getUTCDate());
                }
                return dueDateTime;
              }
              if (typeof dueDateTime === 'string') {
                if (dueDateTime.match(/^\d{4}-\d{2}-\d{2}$/)) {
                  const [year, month, day] = dueDateTime.split('-').map(Number);
                  return new Date(year, month - 1, day);
                } else {
                  const isoDate = new Date(dueDateTime);
                  return new Date(isoDate.getUTCFullYear(), isoDate.getUTCMonth(), isoDate.getUTCDate());
                }
              }
              return undefined;
            };
            const dueDate = parseTodoDueDate(todo.dueDateTime);
            return dueDate ? (
              <div className={`${pastDue ? (isDarkMode ? 'text-red-400' : 'text-red-600') : theme.textMuted} text-xs mt-0.5 font-semibold`}>
                {pastDue ? 'Past due' : formatDate(dueDate, 'short')}
              </div>
            ) : null;
          })()}
          {todo.assigneeName && (() => {
            const assigneeMember = todo.assigneeId 
              ? familyMembers.find(m => m.userId === todo.assigneeId)
              : null;
            const assigneeColor = assigneeMember?.color || '#3b82f6';
            const memberForAvatar = assigneeMember
              ? {
                  memberId: assigneeMember.userId,
                  memberName: assigneeMember.name,
                  memberColor: assigneeMember.color,
                  memberAvatar: assigneeMember.avatar,
                }
              : {
                  memberId: todo.assigneeId,
                  memberName: todo.assigneeName,
                  memberColor: assigneeColor,
                  memberAvatar: undefined,
                };
            return (
              <div className="flex items-center gap-1.5 mt-0.5">
                <MemberAvatar
                  member={memberForAvatar}
                  size="sm"
                />
                <span className={`${theme.textSecondary} text-xs`}>{memberForAvatar.memberName}</span>
              </div>
            );
          })()}
        </div>
        {todo.priority && todo.priority !== 'medium' && (
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 ${
            todo.priority === 'critical' ? 'bg-red-500 text-white' :
            todo.priority === 'high' ? 'bg-orange-500 text-white' :
            'bg-gray-500 text-white'
          }`}>
            {todo.priority}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      {/* Two-column layout: Events on left, Weather on right */}
      <div className="flex-1 flex flex-col md:flex-row gap-3 sm:gap-4 overflow-hidden">
        {/* Events column - scrollable */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-2 sm:px-4 pb-4 min-w-0">
        {/* Empty state - only show if no events AND no todos */}
        {dayEvents.length === 0 && dayTodos.length === 0 && (
          <div 
            className="flex-1 flex items-center justify-center p-8 cursor-pointer min-h-[400px]"
            onClick={() => {
              const now = new Date();
              const selectedHour = isDateToday ? now.getHours() : 9;
              onDateClick?.(date, selectedHour);
            }}
          >
            <div className={`text-center ${theme.textMuted} max-w-md`}>
              <div className="text-6xl sm:text-7xl mb-6">📋</div>
              <div className={`text-xl sm:text-2xl font-bold mb-3 ${theme.text}`}>Nothing scheduled for this day</div>
              <div className="text-base sm:text-lg mb-4">Click here to add your first event</div>
              <div className={`text-xs sm:text-sm ${theme.textMuted} mt-4`}>
                {formatDate(date, 'long')}
              </div>
            </div>
          </div>
        )}

        {/* Events and Todos list */}
        {(dayEvents.length > 0 || dayTodos.length > 0) && (
          <div className="space-y-4">
            {/* All-Day Events Section */}
            {allDayEvents.length > 0 && (
              <div className="mb-6">
                <h2 className={`${theme.sectionHeader} text-lg sm:text-xl font-bold mb-3 px-2`}>
                  All Day
                </h2>
                <div className="space-y-2">
                  {allDayEvents.map((event) => {
                    return (
                      <div key={event.id}>
                        {renderAllDayEvent(event)}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Multi-Day Timed Events Section */}
            {multiDayTimedEvents.length > 0 && (
              <div className="mb-6">
                <h2 className={`${theme.sectionHeader} text-xl sm:text-2xl font-bold mb-4 px-2`}>
                  Ongoing Events
                </h2>
                <div className="space-y-3 sm:space-y-4">
                  {multiDayTimedEvents.map((event) => {
                    const eventIsPast = isEventPast(event);
                    return (
                      <div key={event.id} className={eventIsPast ? 'space-y-2' : ''}>
                        {renderEvent(event)}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Events Today Section */}
            {todayOnlyEvents.length > 0 && (
              <div>
                <h2 className={`${theme.sectionHeader} text-xl sm:text-2xl font-bold mb-4 px-2`}>
                  {isDateToday ? 'Today' : 'Events'}
                </h2>
                <div className="space-y-3 sm:space-y-4">
                  {todayOnlyEvents.map((event) => {
                    const eventIsPast = isEventPast(event);
                    return (
                      <div key={event.id} className={eventIsPast ? 'space-y-2' : ''}>
                        {renderEvent(event)}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Todos Section */}
            {dayTodos.length > 0 && (
              <div className={dayEvents.length > 0 ? 'mt-6' : ''}>
                <h2 className={`${theme.sectionHeader} text-xl sm:text-2xl font-bold mb-4 px-2`}>
                  To Dos
                </h2>
                <div className="space-y-2">
                  {dayTodos.map(renderTodo)}
                </div>
              </div>
            )}
          </div>
        )}
        </div>

        {/* Weather column - slightly narrower for sidebar comfort */}
        <div className="flex-shrink-0 w-full md:w-[224px] lg:w-[252px] h-full flex flex-col px-2 sm:px-4 md:px-0 pb-4 md:pb-0">
          <div className="h-full flex flex-col">
            <WeatherWidget 
              isDarkMode={isDarkMode} 
              location="family"
              variant="large"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
